import { HeadToHeadCardStyle, CMS_CONTRACT_AFFILIATIONS as AFFILIATIONS } from '../cms-config.js';
import { priceCents, safeColor, firstText } from '../cms-market-helpers.js';

export function splitShares(values) {
  if (values.some((value) => value === null || !Number.isFinite(value) || value < 0)) {
    return values.map(() => 0);
  }
  const total = values.reduce((sum, value) => sum + value, 0);
  return values.map((value) => (total > 0 ? (value / total) * 100 : 0));
}

export function resolveHeadToHeadSettings(list = [], uuid, index) {
  return (
    list.find((item) => (uuid ? item.regionUUID === uuid : item.headToHeadIndex === index)) || null
  );
}

export function canTrade(market, contract) {
  if (!market || !contract) return false;
  const unavailable = /^(closed|halted|suspended|settled|maintenance|expired|unavailable)$/i;
  return [market, contract].every(
    (item) =>
      item.isOpen !== false &&
      item.contractIsOpen !== false &&
      item.isActive !== false &&
      item.isHalted !== true &&
      !unavailable.test(item.status || ''),
  );
}

export function contrastInk(color) {
  const rgb = safeColor(color)
    .slice(1)
    .match(/../g)
    .map((part) => {
      const channel = parseInt(part, 16) / 255;
      return channel <= 0.04045 ? channel / 12.92 : ((channel + 0.055) / 1.055) ** 2.4;
    });
  const luminance = rgb[0] * 0.2126 + rgb[1] * 0.7152 + rgb[2] * 0.0722;
  return luminance > 0.179 ? '#000000' : '#ffffff';
}

export function buildHeadToHead(settings, market, showOdds = true) {
  if (!settings || !market) return null;
  const contracts = market.contracts || [];
  const left = contracts.find((item) => item.contractId === Number(settings.leftContract?.id));
  const right = contracts.find((item) => item.contractId === Number(settings.rightContract?.id));
  if (!left || !right || left.contractId === right.contractId) return null;
  const style = Number(settings.cardStyle);
  if (!Object.values(HeadToHeadCardStyle).includes(style)) return null;
  const odds =
    Number(settings.priceDisplay) === 2 || (Number(settings.priceDisplay || 0) === 0 && showOdds);
  const currentPriceSymbol = odds ? '%' : '¢';
  const side = (contract, overrides) => {
    const party = AFFILIATIONS[Number(overrides?.affiliation)];
    const name = firstText(overrides?.name, contract.contractShortName, contract.contractName);
    const price = priceCents(contract.lastTradePrice);
    const color = safeColor(overrides?.colorHex, party?.color || '#6B7280');
    const quote = priceCents(contract.bestYesPrice);
    const buyPrice = quote > 0 ? quote : null;
    return {
      id: contract.contractId,
      isSettled: contract.isSettled,
      isWinner: contract.isWinner,
      settlementDate: contract.settlementDate,
      name,
      partyLabel: party?.label || '',
      buyName: party?.buyName || name,
      isIncumbent: overrides?.isIncumbent === true,
      image: firstText(overrides?.imagePath, contract.contractImageUrl),
      color,
      ink: contrastInk(color),
      price,
      display: price === null ? '—' : `${price}${currentPriceSymbol}`,
      buyPrice,
      buyDisplay: buyPrice === null ? '' : `${buyPrice}${currentPriceSymbol}`,
    };
  };
  const l = side(left, settings.leftContract);
  const r = side(right, settings.rightContract);
  const remaining = contracts.filter(
    (item) => ![left.contractId, right.contractId].includes(item.contractId),
  );
  const prices = remaining.map((item) => priceCents(item.lastTradePrice));
  const other = prices.includes(null) ? null : prices.reduce((sum, value) => sum + value, 0);
  return {
    marketId: market.marketId,
    marketStatus: market.status,
    style,
    odds,
    currentPriceSymbol,
    left: l,
    right: r,
    other,
    title: firstText(settings.market?.name, market.marketShortName, market.marketName),
    image: firstText(settings.market?.imagePath, market.marketImageUrl),
    date: typeof settings.market?.eventDate === 'string' ? settings.market.eventDate : '',
    shares: splitShares(
      style === HeadToHeadCardStyle.SplitBarChunky ? [l.price, other, r.price] : [l.price, r.price],
    ),
  };
}
